import React, { JSX, ReactNode } from 'react';
import { X, Database, Loader, CheckCircle, AlertCircle, Zap, FileText } from 'lucide-react';

  interface LogEvent {
  type: 'info' | 'json' | 'table';
  icon: ReactNode;
  label: string;
  content: string | Record<string, any> | string[][];
}

interface Props {
  nodeName: string;
  nodeTime: string;
  onClose: () => void;
  status: 'Success' | 'Failed' | 'Running';
  events: LogEvent[];
}

const DetailedNodeLogModal: React.FC<Props> = ({ nodeName, nodeTime, onClose, status, events }) => {
  const statusColor = {
    Success: 'text-green-400',
    Failed: 'text-red-400',
    Running: 'text-yellow-300',
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex justify-center items-start pt-14 px-4">
      <div className="bg-neutral-900 w-full max-w-2xl rounded-2xl shadow-2xl border border-neutral-800 overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-neutral-800">
          <div>
            <h2 className="text-white text-lg font-semibold">{nodeName}</h2>
            <div className="text-xs text-gray-400">{nodeTime}</div>
          </div>
          <div className={`text-sm ${statusColor[status]}`}>{status}</div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5 max-h-[70vh] overflow-auto">
          {events.map((e, idx) => (
            <div key={idx} className="flex items-start gap-4">
              <div className="mt-1">{e.icon}</div>
              <div className="text-sm text-white w-full">
                <div className="font-medium mb-1">{e.label}</div>
                {e.type === 'info' && <p className="text-gray-300">{e.content as string}</p>}
                {e.type === 'json' && (
                  <pre className="bg-neutral-800 border border-neutral-700 rounded p-3 text-xs text-gray-300 overflow-x-auto">
                    {JSON.stringify(e.content, null, 2)}
                  </pre>
                )}
                {e.type === 'table' && (
                  <table className="text-xs text-gray-300 w-full border border-neutral-700 rounded overflow-auto">
                    <tbody>
                      {(e.content as string[][]).map((row, i) => (
                        <tr key={i}>
                          {row.map((cell, j) => (
                            <td key={j} className="border border-neutral-700 px-2 py-1">{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-800 bg-neutral-950 text-right">
          <button onClick={onClose} className="text-gray-400 hover:text-white text-sm">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DetailedNodeLogModal;
